import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SysManagementService, UserSearchResult, ManagerData, ManagementLevelHistory, RobotIdItem } from '../../services/sys-management.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-sys-privilege',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sys-privilege.html',
  styleUrl: './sys-privilege.scss'
})
export class SysPrivilegeComponent implements OnInit {

  // 特権管理のプロパティ
  searchEmail: string = '';
  selectedUser: UserSearchResult | null = null;
  newManagementLevel: string = '1';
  isEmergencyApplication: boolean = false;
  srNumber: string = '';
  searchErrorMessage: string = '';  // 検索エラーメッセージ

  // 管理者レベル関連
  adminLevel: number = 0;
  availableLevels: number[] = [];

  // 古いデータ保持用（変更前の値を保存）
  originalAdminLevel: number = 1;
  originalReadWrite: boolean = false;

  // フィルター設定（初期状態はすべて未選択）
  filterLevel1: boolean = false;
  filterLevel2: boolean = false;
  filterLevel3: boolean = false;
  filterLevel4: boolean = false;
  filterLevel5: boolean = false;
  filterLevel9: boolean = false;
  filterErrorMessage: string = '';  // フィルターエラーメッセージ

  // 管理者リスト
  managers: ManagerData[] = [];

  // 管理レベル変更履歴
  selectedYear: number = new Date().getFullYear();
  yearOptions: number[] = [];
  managementLevelHistory: ManagementLevelHistory[] = [];
  historyErrorMessage: string = '';

  // ロボット用ID登録
  robotLoginId: string = '';
  robotSrNumber: string = '';
  robotErrorMessage: string = '';
  showRobotIdList: boolean = false;
  robotIds: RobotIdItem[] = [];

  // 入力範囲の設定
  currentYear: number = new Date().getFullYear();

  constructor(
    private sysManagementService: SysManagementService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.adminLevel = this.authService.getAdminLevel() || 0;
    this.initializeAvailableLevels();
    this.initializeYearOptions();
  }

  // 利用可能な管理者レベルを初期化
  private initializeAvailableLevels(): void {
    if (this.adminLevel >= 9) {
      // AdminLevel 9以上: レベル1〜5,9まで
      this.availableLevels = [1, 2, 3, 4, 5, 9];
    } else if (this.adminLevel >= 5) {
      // AdminLevel 5〜8: レベル1〜5まで
      this.availableLevels = [1, 2, 3, 4, 5];
    } else {
      // その他：デフォルト（1〜4のみ）
      this.availableLevels = [1, 2, 3, 4];
    }
  }

  // 年選択オプションを初期化
  private initializeYearOptions(): void {
    this.yearOptions = [
      this.currentYear,      // 今年
      this.currentYear - 1   // 昨年
    ];
    this.selectedYear = this.currentYear;  // 今年を初期選択
  }

  // 管理者リストを読み込み（現在は抽出ボタン押下時にのみデータ取得するため空）
  loadManagers(): void {
    // 初期化時は何もしない（ボタン押下時にデータ取得）
    console.log('管理者リスト初期化完了');
  }

  // ユーザー検索
  searchUser(): void {
    // エラーメッセージをクリア
    this.searchErrorMessage = '';
    this.selectedUser = null;

    // メールアドレス入力チェック
    if (!this.searchEmail || this.searchEmail.trim() === '') {
      this.searchErrorMessage = 'メールアドレスを入力してください';
      return;
    }

    // 簡単なメール形式チェック
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(this.searchEmail.trim())) {
      this.searchErrorMessage = '正しいメールアドレスの形式で入力してください';
      return;
    }

    // API呼び出しでユーザー検索
    this.sysManagementService.getUserByEmail(this.searchEmail.trim(), true)
      .subscribe({
        next: (user: UserSearchResult | null) => {
          if (user) {
            this.selectedUser = user;
            this.newManagementLevel = user.adminLevel?.toString() || '1';
            // 検索結果のReadWrite値を書込み権限に反映
            this.isEmergencyApplication = user.readWrite;

            // 古いデータ（原値）を保存
            this.originalAdminLevel = user.adminLevel;
            this.originalReadWrite = user.readWrite;

            this.searchErrorMessage = '';
          } else {
            this.searchErrorMessage = '指定されたメールアドレスのユーザーが見つかりませんでした';
            this.selectedUser = null;
          }
        },
        error: () => {
          this.searchErrorMessage = 'ユーザー検索に失敗しました';
          this.selectedUser = null;
        }
      });
  }

  // ユーザーレベル更新
  updateUserLevel(): void {
    if (!this.selectedUser) {
      alert('ユーザーを選択してください');
      return;
    }

    if (!this.srNumber || this.srNumber.trim() === '') {
      alert('変更申請SR番号を入力してください');
      return;
    }

    const updateData = {
      UserID: parseInt(this.selectedUser.userID),     // UserID (PascalCase)
      OldLevel: this.originalAdminLevel,              // 古い管理者レベル
      NewLevel: parseInt(this.newManagementLevel),    // 新しい管理者レベル
      TicketNo: this.srNumber.trim(),                 // SR番号
      OldReadWrite: this.originalReadWrite,           // 古い書込み権限
      NewReadWrite: this.isEmergencyApplication       // 新しい書込み権限
    };

    console.log('管理レベル更新リクエスト:', updateData);

    this.sysManagementService.updateUserManagementLevel(updateData)
      .subscribe({
        next: (result: any) => {
          alert('管理者レベルを更新しました');
          console.log('レベル更新完了:', result);
          // ユーザー情報を再取得
          this.searchUser();
          // フォームをクリア
          this.srNumber = '';
        },
        error: (error: any) => {
          const errorMessage = error?.error?.message || error?.message || '管理者レベル更新に失敗しました';
          alert(`エラー: ${errorMessage}`);
        }
      });
  }

  // 社員管理者一覧CSVエクスポート
  exportManagersCSV(): void {
    this.filterErrorMessage = '';

    // 選択されたレベルを収集
    const selectedLevels: number[] = [];
    if (this.filterLevel1) selectedLevels.push(1);
    if (this.filterLevel2) selectedLevels.push(2);
    if (this.filterLevel3) selectedLevels.push(3);
    if (this.filterLevel4) selectedLevels.push(4);
    if (this.filterLevel5) selectedLevels.push(5);
    if (this.filterLevel9) selectedLevels.push(9);

    if (selectedLevels.length === 0) {
      this.filterErrorMessage = 'レベルを1つ以上選択してください';
      return;
    }

    // サービスから管理者データを取得
    this.sysManagementService.getManagersByLevels(selectedLevels).subscribe({
      next: (managers: ManagerData[]) => {
        this.managers = managers;

        if (this.managers.length === 0) {
          alert('出力するデータがありません');
          return;
        }

        // CSV形式でデータを作成
        const csvHeaders = ['UserID', '氏名', '管理レベル', '書込み', 'Email', '作成日時', '更新日時'];
        const csvData = this.managers.map(manager => [
          manager.userID,
          manager.userName,
          manager.adminLevel.toString(),
          manager.readWrite ? '1' : '',
          manager.email,
          manager.createDate,
          manager.updateDate
        ]);

        const csvContent = [csvHeaders, ...csvData]
          .map(row => row.map(field => `"${field}"`).join(','))
          .join('\n');

        // BOM付きUTF-8でファイルダウンロード
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `managers_level_${selectedLevels.join('_')}.csv`;
        link.click();
        URL.revokeObjectURL(url);

        console.log('社員管理者一覧CSV出力完了:', this.managers.length + '件');
      },
      error: () => {
        this.filterErrorMessage = '社員管理者一覧の取得に失敗しました';
        this.managers = [];
      }
    });
  }

  // 管理レベル変更履歴CSVエクスポート
  exportHistoryCSV(): void {
    this.historyErrorMessage = '';

    this.sysManagementService.getManagementLevelHistory(this.selectedYear)
      .subscribe({
        next: (history: ManagementLevelHistory[]) => {
          this.managementLevelHistory = history;

          if (this.managementLevelHistory.length === 0) {
            alert('出力するデータがありません');
            return;
          }

          // CSV形式でデータを作成
          const csvHeaders = ['UserID', '氏名', 'レベル', '書込み', 'SR番号', '処理者ID', '処理日時'];
          const csvData = this.managementLevelHistory.map(history => [
            history.userID,
            history.userName,
            history.newLevel.toString(),
            history.newReadWrite ? '1' : '',
            history.ticketNo,
            history.operaterID,
            history.createDate
          ]);

          const csvContent = [csvHeaders, ...csvData]
            .map(row => row.map(field => `"${field}"`).join(','))
            .join('\n');

          // BOM付きUTF-8でファイルダウンロード
          const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
          const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `management_level_history_${this.selectedYear}.csv`;
          link.click();
          URL.revokeObjectURL(url);

          console.log('管理レベル変更履歴CSV出力完了:', this.managementLevelHistory.length + '件');
        },
        error: (error: any) => {
          const statusText = error.status ? ` (${error.status})` : '';
          const errorMsg = error.error?.message || error.message || '不明なエラー';
          this.historyErrorMessage = `管理レベル変更履歴の取得に失敗しました${statusText}: ${errorMsg}`;
          this.managementLevelHistory = [];
        }
      });
  }

  // ロボット用ID登録: 一覧表示
  loadRobotIdList(): void {
    this.robotErrorMessage = '';
    this.sysManagementService.getRobotIds().subscribe({
      next: (items: RobotIdItem[]) => {
        this.robotIds = items;
        this.showRobotIdList = true;
      },
      error: () => {
        this.robotErrorMessage = 'ロボット用IDの取得に失敗しました';
        this.robotIds = [];
        this.showRobotIdList = true;
      }
    });
  }

  // ロボット用ID登録: 登録
  registerRobotId(): void {
    this.robotErrorMessage = '';
    if (!this.robotLoginId.trim()) {
      this.robotErrorMessage = 'LoginIDを入力してください';
      return;
    }
    if (!this.robotSrNumber.trim()) {
      this.robotErrorMessage = '登録申請SR番号を入力してください';
      return;
    }

    const loginId = this.robotLoginId.trim();
    const srNumber = this.robotSrNumber.trim();
    setTimeout(() => {
      if (!confirm(`LoginID「${loginId}」を登録します。よろしいですか？`)) {
        return;
      }

      this.sysManagementService.registerRobotId(loginId, srNumber)
        .subscribe((result) => {
          if (!result.success) {
            // 重複など（409 Conflict）はアラートで通知して中断
            alert(result.message);
            return;
          }
          alert('IDを登録しました。');
          this.resetRobotForm();
        });
    }, 0);
  }

  // ロボット用ID登録: 削除
  deleteRobotId(): void {
    this.robotErrorMessage = '';
    if (!this.robotLoginId.trim()) {
      this.robotErrorMessage = 'LoginIDを入力してください';
      return;
    }

    const loginId = this.robotLoginId.trim();
    setTimeout(() => {
      if (!confirm(`LoginID「${loginId}」を削除します。よろしいですか？`)) {
        return;
      }

      this.sysManagementService.deleteRobotId(loginId)
        .subscribe((result) => {
          if (!result.success) {
            this.robotErrorMessage = result.message;
            return;
          }
          alert('削除しました。');
          this.resetRobotForm();
        });
    }, 0);
  }

  // ロボット用IDフォームを初期状態に戻す
  private resetRobotForm(): void {
    this.robotLoginId = '';
    this.robotSrNumber = '';
    this.robotErrorMessage = '';
    this.showRobotIdList = false;
    this.robotIds = [];
  }

}
